import { type ApiEnvelope, api, unwrap } from "@/lib/api";

import type {
	Anomaly,
	AuditLogEntry,
	BudgetRow,
	ChartAccount,
	CreditNote,
	ExportJob,
	FinanceDashboard,
	Invoice,
	InvoiceFilter,
	Journal,
	JournalEntry,
	LedgerResult,
	NewInvoicePayload,
	PaginatedInvoices,
	TaxPeriod,
	TrialBalance,
	VatPreview,
	VatReturn,
} from "./types";

const toQs = (filter: Record<string, unknown>): string => {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(filter)) {
		if (v === undefined || v === null || v === "") continue;
		usp.set(k, String(v));
	}
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

const BASE = "/admin/finance";

export const financeApi = {
	// FIN-01
	dashboard: () =>
		unwrap<FinanceDashboard>(
			api.get<ApiEnvelope<FinanceDashboard>>(`${BASE}/dashboard`),
		),

	// FIN-02/03
	invoices: (filter: InvoiceFilter = {}) =>
		unwrap<PaginatedInvoices>(
			api.get<ApiEnvelope<PaginatedInvoices>>(
				`${BASE}/invoices${toQs(filter as Record<string, unknown>)}`,
			),
		),
	invoice: (id: string) =>
		unwrap<Invoice>(api.get<ApiEnvelope<Invoice>>(`${BASE}/invoices/${id}`)),
	issueInvoice: (payload: NewInvoicePayload) =>
		unwrap<Invoice>(
			api.post<ApiEnvelope<Invoice>>(`${BASE}/invoices`, payload),
		),
	markPaid: (id: string, amount: number) =>
		unwrap<Invoice>(
			api.post<ApiEnvelope<Invoice>>(`${BASE}/invoices/${id}/mark-paid`, {
				amount,
			}),
		),
	convertProforma: (id: string) =>
		unwrap<Invoice>(
			api.post<ApiEnvelope<Invoice>>(
				`${BASE}/invoices/${id}/convert-proforma`,
				{},
			),
		),
	invoicePdfUrl: (id: string) =>
		`${api.defaults.baseURL}${BASE}/invoices/${id}/pdf`,
	openInvoicePdf: async (id: string, invoiceNo: string) => {
		const res = await api.get(`${BASE}/invoices/${id}/pdf`, {
			responseType: "blob",
		});
		const url = URL.createObjectURL(res.data as Blob);
		const a = document.createElement("a");
		a.href = url;
		a.target = "_blank";
		a.rel = "noreferrer";
		a.download = `${invoiceNo}.pdf`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 30_000);
	},

	// FIN-04
	creditNotes: () =>
		unwrap<CreditNote[]>(
			api.get<ApiEnvelope<CreditNote[]>>(`${BASE}/credit-notes`),
		),
	createCreditNote: (payload: {
		invoiceId: string;
		reason: string;
		reasonDetail?: string;
		totalHt: number;
		totalVat: number;
		refundMethod?: string;
	}) =>
		unwrap<CreditNote>(
			api.post<ApiEnvelope<CreditNote>>(`${BASE}/credit-notes`, payload),
		),

	// FIN-05
	journals: () =>
		unwrap<Journal[]>(api.get<ApiEnvelope<Journal[]>>(`${BASE}/journals`)),
	journalEntries: (code: string, periodId?: string) =>
		unwrap<JournalEntry[]>(
			api.get<ApiEnvelope<JournalEntry[]>>(
				`${BASE}/journals/${code}/entries${periodId ? `?periodId=${periodId}` : ""}`,
			),
		),
	entry: (id: string) =>
		unwrap<JournalEntry>(
			api.get<ApiEnvelope<JournalEntry>>(`${BASE}/entries/${id}`),
		),
	postManualEntry: (payload: {
		postedAt: string;
		label: string;
		reference?: string;
		lines: Array<{
			account: string;
			debit?: number;
			credit?: number;
			label?: string;
		}>;
	}) =>
		unwrap<JournalEntry>(
			api.post<ApiEnvelope<JournalEntry>>(`${BASE}/entries`, payload),
		),
	reverseEntry: (id: string, reason: string) =>
		unwrap<JournalEntry>(
			api.post<ApiEnvelope<JournalEntry>>(`${BASE}/entries/${id}/reverse`, {
				reason,
			}),
		),

	// FIN-06
	chartOfAccounts: () =>
		unwrap<ChartAccount[]>(
			api.get<ApiEnvelope<ChartAccount[]>>(`${BASE}/chart-of-accounts`),
		),
	ledger: (code: string) =>
		unwrap<LedgerResult>(
			api.get<ApiEnvelope<LedgerResult>>(
				`${BASE}/chart-of-accounts/${code}/ledger`,
			),
		),
	unlettered: () =>
		unwrap<unknown[]>(api.get<ApiEnvelope<unknown[]>>(`${BASE}/unlettered`)),

	// FIN-09 / périodes
	trialBalance: (periodId?: string) =>
		unwrap<TrialBalance>(
			api.get<ApiEnvelope<TrialBalance>>(
				`${BASE}/trial-balance${periodId ? `?periodId=${periodId}` : ""}`,
			),
		),
	periods: (fiscalYear?: number) =>
		unwrap<TaxPeriod[]>(
			api.get<ApiEnvelope<TaxPeriod[]>>(
				`${BASE}/periods${fiscalYear ? `?fiscalYear=${fiscalYear}` : ""}`,
			),
		),
	lockPeriod: (id: string) =>
		unwrap<TaxPeriod>(
			api.post<ApiEnvelope<TaxPeriod>>(`${BASE}/periods/${id}/lock`, {}),
		),
	unlockPeriod: (id: string, reason: string) =>
		unwrap<TaxPeriod>(
			api.post<ApiEnvelope<TaxPeriod>>(`${BASE}/periods/${id}/unlock`, {
				reason,
			}),
		),

	// FIN-07
	vatReturns: () =>
		unwrap<VatReturn[]>(
			api.get<ApiEnvelope<VatReturn[]>>(`${BASE}/vat-returns`),
		),
	vatPreview: (periodId: string) =>
		unwrap<VatPreview>(
			api.get<ApiEnvelope<VatPreview>>(
				`${BASE}/vat-returns/preview?periodId=${periodId}`,
			),
		),
	vatBuild: (periodId: string) =>
		unwrap<VatReturn>(
			api.post<ApiEnvelope<VatReturn>>(
				`${BASE}/vat-returns/build?periodId=${periodId}`,
				{},
			),
		),
	vatSubmit: (id: string, dgiReceiptNo?: string) =>
		unwrap<VatReturn>(
			api.post<ApiEnvelope<VatReturn>>(`${BASE}/vat-returns/${id}/submit`, {
				dgiReceiptNo,
			}),
		),

	// FIN-08
	runExport: (payload: {
		format: string;
		periodFrom: string;
		periodTo: string;
		journals?: string[];
	}) =>
		unwrap<ExportJob>(
			api.post<ApiEnvelope<ExportJob>>(`${BASE}/exports`, payload),
		),
	exportStatus: (jobId: string) =>
		unwrap<ExportJob>(
			api.get<ApiEnvelope<ExportJob>>(`${BASE}/exports/${jobId}`),
		),
	exportDownloadUrl: (jobId: string) =>
		`${api.defaults.baseURL}${BASE}/exports/${jobId}/download`,
	downloadExport: async (jobId: string, filename: string) => {
		const res = await api.get(`${BASE}/exports/${jobId}/download`, {
			responseType: "blob",
		});
		const url = URL.createObjectURL(res.data as Blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 30_000);
	},

	// FIN-10
	budgets: (fiscalYear?: number) =>
		unwrap<BudgetRow[]>(
			api.get<ApiEnvelope<BudgetRow[]>>(
				`${BASE}/budgets${fiscalYear ? `?fiscalYear=${fiscalYear}` : ""}`,
			),
		),
	saveBudgets: (
		fiscalYear: number,
		rows: Array<{ accountCode: string; month: number; amountBudget: number }>,
	) =>
		unwrap<{ count: number }>(
			api.post<ApiEnvelope<{ count: number }>>(`${BASE}/budgets/batch`, {
				fiscalYear,
				rows,
			}),
		),

	// FIN-11
	auditLog: (limit = 100) =>
		unwrap<AuditLogEntry[]>(
			api.get<ApiEnvelope<AuditLogEntry[]>>(`${BASE}/audit/log?limit=${limit}`),
		),
	anomalies: () =>
		unwrap<Anomaly[]>(
			api.get<ApiEnvelope<Anomaly[]>>(`${BASE}/audit/anomalies`),
		),
};
